import React from "react";
import { Collapse, Input } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Controller } from "react-hook-form";
import BaseFormField from "./BaseFormField";

/**
 * Form field frame — the `.onbarding-input` look, kept in lockstep with the
 * matching FIELD block in BaseDropdown so the text field and select read
 * identically. Colors are colors_v2 CSS custom properties so the field stays
 * theme-driven.
 */
const FIELD = {
  height: 40,
  radius: "6px",
  bg: "var(--input-background-default)",
  border: "var(--new-border)",
  // Hover steps one shade darker (--new-border-accent), focus to
  // --font-heavy-default with no ring; matches BaseDropdown exactly.
  borderHover: "var(--new-border-accent)",
  borderFocus: "var(--font-heavy-default)",
  text: "var(--input-content-filled)",
  placeholder: "var(--input-content-empty)",
  errorBorder: "var(--error-error-80)",
  disabledBorder: "var(--app-layer-accent-01)",
  disabledText: "var(--font-strong-default)"
};

/**
 * BaseTextField — the canonical reskin text field (promoted from
 * FormTextFieldReskin, behavior unchanged).
 *
 * WHAT IT IS: a labelled, bordered text field (the `.onbarding-input` look)
 * wired to react-hook-form via `Controller`, with the four validation/error
 * rows (required, matchPattern, VAT valid, VAT invalid) the company-info flow
 * needs. The frame + interaction styling lives on the component itself (the
 * `inputSx` block below), so the file is self-contained and copy-pastes into
 * another project without dragging a separate stylesheet along.
 *
 * WHAT IT WRAPS: a MUI `Input` (the control) inside `BaseFormField` (label +
 * error scaffold).
 *
 * CONTRACT (props in):
 *  - label: i18n id; doubles as the field `name`, `id`, and Controller key.
 *  - control: react-hook-form control.
 *  - errors: rhf errors object (read as errors[label]?.type).
 *  - required, disabled, placeholder, inputProps: forwarded to the Input.
 *  - validation: object of named validate fns merged into Controller rules.
 *  - validationMessage: node shown when the matchPattern rule fails.
 *  - vatIdValidation: { valid, country } — drives the VAT success/error rows.
 *  - value: the current value, used to gate the VAT rows (they only show once
 *      the user has typed something).
 *  - errorTestId: data-testid on the required-error row.
 *  - forceError: bool — paints the red error border without rendering any of
 *      the built-in error rows, for callers that show their own error content
 *      (e.g. a password rule checklist) below the field.
 *  - tooltip: forwarded to BaseFormField.
 *  - ...props: spread onto the Input (e.g. type).
 *
 * BEHAVIOR (out): identical to the pre-base FormTextFieldReskin — same markup,
 * same validation messages, same data-testids.
 *
 * MIGRATION NOTE (shadcn): the Controller + Input pair becomes
 * <FormField render={({field}) => <Input {...field}/>}>; the error Collapse rows
 * become <FormMessage> conditionals. The scaffold is already factored out into
 * BaseFormField, so only this control + the error rows need porting. The frame
 * styling becomes Tailwind classes on <Input>; the color tokens already come
 * from FIELD.* so they carry over unchanged.
 */

// Frame + interaction styling for the .onbarding-input field, moved off the
// global stylesheet so the component owns it. `error` (the MUI `error` prop)
// adds .Mui-error to the root, so the error/error-focus borders key off that.
// !important mirrors the original SCSS — it wins over MUI's standard-variant
// resets and the global token rules.
const inputSx = {
  marginTop: "0 !important",
  boxSizing: "border-box",
  height: FIELD.height,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px",
  padding: "12px 16px !important",
  border: `solid 1px ${FIELD.border} !important`,
  borderRadius: `${FIELD.radius} !important`,
  backgroundColor: `${FIELD.bg} !important`,
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  // Hover only paints in the resting state — focus and error borders take
  // priority, so guard hover against those states.
  "&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)": {
    borderColor: `${FIELD.borderHover} !important`
  },
  "&.Mui-focused": {
    borderColor: `${FIELD.borderFocus} !important`
  },
  "&.Mui-error": {
    borderColor: `${FIELD.errorBorder} !important`
  },
  // Error must win even when the field is focused at the same time (e.g. the
  // user clicks into an already-invalid field) — same specificity as
  // `&.Mui-focused` above, so it needs the combined selector to guarantee it
  // wins regardless of object-key order.
  "&.Mui-focused.Mui-error": {
    borderColor: `${FIELD.errorBorder} !important`
  },
  // Disabled: accent-01 border, no hover, not-allowed cursor on the whole field.
  "&.Mui-disabled": {
    borderColor: `${FIELD.disabledBorder} !important`,
    backgroundColor: `${FIELD.bg} !important`,
    cursor: "not-allowed"
  },
  // The bordered root supplies the fill; the inner <input> is transparent so it
  // never paints a second rectangle (e.g. over an eye-icon endAdornment).
  "& .MuiInput-input": {
    height: "auto !important",
    padding: "0 !important",
    borderRadius: "0 !important",
    background: "transparent !important",
    fontFamily: "Inter, sans-serif !important",
    fontSize: "14px !important",
    fontWeight: "400 !important",
    lineHeight: "1.57 !important",
    letterSpacing: "normal !important",
    color: `${FIELD.text} !important`
  },
  "& .MuiInput-input::placeholder": {
    color: `${FIELD.placeholder} !important`,
    opacity: 1
  },
  // The disabled <input> can keep the text caret under MUI's reset — pin
  // not-allowed so the whole field reads as disabled.
  "&.Mui-disabled .MuiInput-input": {
    cursor: "not-allowed !important",
    // Disabled text & placeholder: --font-strong-default at half opacity. MUI
    // sets its own .Mui-disabled opacity, so pin both colour and opacity.
    color: `${FIELD.disabledText} !important`,
    WebkitTextFillColor: `${FIELD.disabledText} !important`,
    opacity: "0.5 !important"
  },
  "&.Mui-disabled .MuiInput-input::placeholder": {
    color: `${FIELD.disabledText} !important`,
    WebkitTextFillColor: `${FIELD.disabledText} !important`,
    opacity: "0.5 !important"
  }
};
const BaseTextField = ({
  errors,
  label,
  required,
  disabled,
  validation,
  validationMessage = "",
  vatIdValidation,
  value = "",
  control,
  errorTestId,
  tooltip,
  placeholder,
  inputProps,
  forceError = false,
  ...props
}) => {
  const hasError =
    forceError ||
    errors[label]?.type === "required" ||
    errors[label]?.type === "matchPattern" ||
    (errors[label]?.type === "vatIdValidation" && value);

  const errorContent = (
    <>
      <Collapse in={errors[label]?.type === "required"}>
        <span className="text-error" data-testid={errorTestId}>
          <FormattedMessage id={label} /> <FormattedMessage id={"isRequired"} />
        </span>
      </Collapse>

      <Collapse in={errors[label]?.type === "matchPattern"}>
        <span className="text-error">{validationMessage}</span>
      </Collapse>

      <Collapse in={Boolean(vatIdValidation?.valid && value)}>
        <span className="text-success">
          <FormattedMessage id="vatIdIsValidFor" />{" "}
          {vatIdValidation?.country && (
            <FormattedMessage id={vatIdValidation.country} />
          )}
        </span>
      </Collapse>

      <Collapse
        in={Boolean(
          !vatIdValidation?.valid &&
            errors[label]?.type === "vatIdValidation" &&
            value
        )}
      >
        <span className="text-error">
          <FormattedMessage id={"vatIdNotCorrect"} />
        </span>
      </Collapse>
    </>
  );

  return (
    <BaseFormField
      label={label}
      required={required}
      tooltip={tooltip}
      errorContent={errorContent}
    >
      <Controller
        name={label}
        control={control}
        rules={{ required: required, validate: { ...validation } }}
        render={({ field: { onChange, onBlur, value: fieldValue, ref } }) => (
          <Input
            id={label}
            name={label}
            // Kept for any external selectors/tests; the styling now lives in
            // `inputSx` on this component, not in a global stylesheet.
            className="onbarding-input"
            disableUnderline
            color="primary"
            inputRef={ref}
            disabled={disabled}
            error={hasError}
            placeholder={placeholder}
            value={fieldValue}
            onChange={onChange}
            // Forward rhf's onBlur so the field is marked touched; required for
            // mode:"onTouched" to surface per-field errors on blur.
            onBlur={onBlur}
            inputProps={inputProps}
            sx={inputSx}
            {...props}
          />
        )}
      />
    </BaseFormField>
  );
};

export default BaseTextField;
